import { Link } from "react-router-dom";
import { usePostApiRequest } from "../../helpers/api";
import { useEffect, useState } from "react";
import LoadingSpinner from "../loading/LoadingSpinner";
import { handleGlobalInputChange } from "../../helpers/functions";
import { useQueryClient } from "@tanstack/react-query";

const colorOptions = [
  { id: "YW", name: "Yellow Gold" },
  { id: "W", name: "White Gold" },
  { id: "PW", name: "Pink" },
];
const caratOptions = [
  { id: "10K", name: "10KT" },
  { id: "14K", name: "14KT" },
  { id: "18K", name: "18KT" },
];
const weightMapping = {
  "18K": 1,
  "14K": 0.875,
  "10K": 0.76,
};

const CartItem = ({
  handleItemSelection,
  selectedItems,
  isBulkEditing,
  cartItem,
  customerId,
  isLoading,
  setIsLoading,
}) => {

  const [calculatedWeight, setCalculatedWeight] = useState("");
  const [formData, setFormData] = useState({
    Carat: cartItem.Carat || "18K",
    Color: cartItem.Color || "YW",
    Size: cartItem.Size,
  });
  const [productQuantity, setProductQuantity] = useState(
    () => cartItem?.Quantity ?? 1
  );
  const postApiRequest = usePostApiRequest();
  const queryClient = useQueryClient();

  useEffect(() => {
    setFormData({
      Carat: cartItem.Carat || "18K",
      Color: cartItem.Color || "YW",
      Size: cartItem.Size,
    });
  }, [cartItem]);

  // handle input chnage with Carat calculation logic
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleGlobalInputChange(e, setFormData);

    // send Weight in api according to different Carat logic
    let updatedCarat = name === "Carat" ? value : formData.Carat;
    let updatedColor = name === "Color" ? value : formData.Color;
    let updatedSize = name === "Size" ? value : formData.Size;
    let newWeight = calculatedWeight;

    if (name === "Carat") {
      newWeight =
        (cartItem.Weight / weightMapping[cartItem.Carat]) *
        weightMapping[value];
    }

    newWeight = parseFloat(newWeight) || 0;
    setCalculatedWeight(newWeight);

    updateCartItem(
      cartItem.Id,
      updatedCarat,
      updatedColor,
      newWeight,
      updatedSize
    );
  };

  // update cart quantity api
  const updateCartQuantity = async (Id, quantity) => {
    console.log("id ", Id);

    setIsLoading(true);
    const data = {
      Id: Id,
      CustomerId: customerId,
      Quantity: quantity,
    };

    try {
      const response = await postApiRequest({
        url: "/Customer/UpdateCartQty",
        method: "POST",
        data: data,
      });

      if (response.Status === true) {
        queryClient.invalidateQueries({
          queryKey: ["cartItemCount"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["cartData"],
          exact: false,
        });
      }
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCartItem = async () => {
    setIsLoading(true);
    const data = {
      Id: cartItem.Id,
      CustomerId: customerId,
    };

    try {
      const response = await postApiRequest({
        url: "/Customer/DeleteCart",
        method: "POST",
        data: data,
      });
      if (response.Status === true) {
        queryClient.invalidateQueries({
          queryKey: ["cartItemCount"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["cartData"],
          exact: false,
        });
      }
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // update variant data and quantity
  useEffect(() => {
    if (formData.Carat && cartItem.Carat) {
      const newWeight =
        (cartItem.Weight / weightMapping[cartItem.Carat]) *
        weightMapping[formData.Carat];

      setCalculatedWeight(newWeight.toFixed(2));
    }
  }, [formData.Carat, cartItem.Carat, cartItem.Weight]);
  useEffect(() => {
    if (cartItem?.Quantity !== undefined) {
      setProductQuantity(cartItem.Quantity);
    }
  }, [cartItem.Quantity]);

  // update variant api
  const updateCartItem = async (
    id,
    updatedCarat,
    updatedColor,
    updatedWeight,
    updatedSize
  ) => {
    setIsLoading(true);
    const data = {
      Id: id,
      CustomerId: customerId,
      Carat: updatedCarat,
      Color: updatedColor,
      Weight: updatedWeight,
      Quantity: cartItem.Quantity,
      Size: updatedSize,
    };

    try {
      await postApiRequest({
        url: "/Customer/EditCart",
        method: "POST",
        data: data,
        queryKey: ["cartData"],
      });
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? <LoadingSpinner /> : ""}
      <div className={`cart_block ${isBulkEditing ? "cart_block_edit" : "cart_block"}`}>
        {isBulkEditing ? (
          <input
            type="checkbox"
            name="cart_item_checkbox"
            className="cart_item_checkbox"
            checked={selectedItems.some((item) => item.Id === cartItem.Id)}
            onChange={(e) => handleItemSelection(cartItem, e.target.checked)}
          />
        ) : (
          ""
        )}
        <div className="product-thumbnail">
          <img
            className="img-fluid"
            src={process.env.REact_APP_IMG_URL + cartItem.ImagePath}
            alt={cartItem.StyleCode}
          />
        </div>

        <div className="product-name">
          <div className="cart_item_content_left">
            <Link to={"/product/" + cartItem.StyleCode}>
              {cartItem.StyleCode}
            </Link>
            {cartItem.Carat ? (
              <div className="cart-item-variation">
                <div className="cart_variant_item">
                  Carat:
                  <select
                    value={formData.Carat}
                    onChange={handleInputChange}
                    name="Carat"
                    id={"product_Carat_" + cartItem.Id}
                    className="select_option"
                  >
                    {caratOptions.map((option) => (
                      <option value={option.id} key={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cart_variant_item">
                  Color:
                  <select
                    value={formData.Color}
                    onChange={handleInputChange}
                    name="Color"
                    id={"product_Color_" + cartItem.Id}
                    className="select_option"
                  >
                    {colorOptions.map((option) => (
                      <option value={option.id} key={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
                {cartItem.ItemSizes.length > 0 && (
                  <div className="cart_variant_item">
                    Size:
                    <select
                      value={formData.Size}
                      onChange={handleInputChange}
                      name="Size"
                      id={"product_Size_" + cartItem.Id}
                      className="select_option"
                    >
                      {cartItem.ItemSizes.map((option) => (
                        <option
                          value={option.CommonMasterCode}
                          key={option.CommonMasterCode}
                        >
                          {option.CommonMasterCode}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              ""
            )}

            <div className="cart-plus-minus">
              <button
                className="dec qtybutton"
                aria-label="Decrease quantity"
                onClick={() => {
                  const newQuantity = Math.max(productQuantity - 1, 1);
                  setProductQuantity(newQuantity);
                  updateCartQuantity(cartItem.Id, newQuantity);
                }}
                disabled={productQuantity === 1}
              >
                -
              </button>
              <input
                className="cart-plus-minus-box"
                type="text"
                value={productQuantity}
                readOnly
                aria-label="Product quantity"
              />
              <button
                className="inc qtybutton"
                aria-label="Increase quantity"
                onClick={() => {
                  const newQuantity = productQuantity + 1;
                  setProductQuantity(newQuantity);
                  updateCartQuantity(cartItem.Id, newQuantity);
                }}
              >
                +
              </button>
            </div>
          </div>
          <div className="cart_item_content_right">
            {cartItem.CustomizeText && (
              <>
                <span>Instructions: </span>
                <span>{cartItem.CustomizeText}</span>
              </>
            )}
          </div>
        </div>

        <div className="product-remove">
          <button
            className="desktop_remove_btn"
            onClick={() => deleteCartItem()}
          >
            Remove
          </button>
          <button className="mobile_remove_btn">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <path
                  d="M20.7457 3.32851C20.3552 2.93798 19.722 2.93798 19.3315 3.32851L12.0371 10.6229L4.74275 3.32851C4.35223 2.93798 3.71906 2.93798 3.32854 3.32851C2.93801 3.71903 2.93801 4.3522 3.32854 4.74272L10.6229 12.0371L3.32856 19.3314C2.93803 19.722 2.93803 20.3551 3.32856 20.7457C3.71908 21.1362 4.35225 21.1362 4.74277 20.7457L12.0371 13.4513L19.3315 20.7457C19.722 21.1362 20.3552 21.1362 20.7457 20.7457C21.1362 20.3551 21.1362 19.722 20.7457 19.3315L13.4513 12.0371L20.7457 4.74272C21.1362 4.3522 21.1362 3.71903 20.7457 3.32851Z"
                  fill="#0F0F0F"
                ></path>
              </g>
            </svg>
          </button>
        </div>

        <div className="estimated_delivery_con">
          <p>ESTIMATED DELIVERY: 15 DAYS</p>
          <div className="cart_item_weight">
            <span>{(cartItem.Weight * cartItem.Quantity).toFixed(2)} GM</span>
            <span>|</span>
            <span>
              {(cartItem.DiamondWt * cartItem.Quantity).toFixed(2)} CT
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartItem;
